import { Link } from '@tanstack/react-router';
import { format, startOfDay } from 'date-fns';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '~shared/shadcn/ui/button';

import { useGetRoomOverview } from '../api/get-room-overview';
import { buildRoomOverviewRowsFromApi } from '../lib/build-room-rows';
import { filterRoomRowsByEmptyRooms } from '../lib/filter-rows';
import { getSelectedInstructorIds } from '../lib/instructor-filter';
import { ALL_INSTRUCTORS_OPTION, type InstructorOption } from '../lib/types';
import { RoomOverviewBoard } from './room-overview-board';
import { RoomOverviewFiltersBar } from './room-overview-filters';
import { RoomOverviewMobileCards } from './room-overview-mobile-cards';

export function RoomOverviewPage() {
  const [selectedDate, setSelectedDate] = useState(() =>
    startOfDay(new Date()),
  );
  const [instructors, setInstructors] = useState<Array<InstructorOption>>([
    ALL_INSTRUCTORS_OPTION,
  ]);
  const [shouldScrollToStart, setShouldScrollToStart] = useState(false);
  const [showEmptyRooms, setShowEmptyRooms] = useState(true);

  const instructorIds = useMemo(
    () => getSelectedInstructorIds(instructors),
    [instructors],
  );

  const dateParam = format(selectedDate, 'yyyy-MM-dd');

  const { data, isLoading, isError } = useGetRoomOverview({
    date: dateParam,
    instructorIds: instructorIds.length > 0 ? instructorIds : undefined,
  });

  const rows = useMemo(() => {
    if (!data) return [];

    return filterRoomRowsByEmptyRooms(
      buildRoomOverviewRowsFromApi(data),
      showEmptyRooms,
    );
  }, [data, showEmptyRooms]);

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load room overview. Please try again.');
    }
  }, [isError]);

  useEffect(() => {
    if (data) {
      setShouldScrollToStart(true);
    }
  }, [data, dateParam, instructorIds]);

  const handleScrolledToStart = () => {
    setShouldScrollToStart(false);
  };

  const isInitialLoading = isLoading && !data;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex h-14 shrink-0 items-center gap-4'>
        <Link to='/dashboard'>
          <Button
            variant='ghost'
            className='flex items-center p-2 [&_svg]:size-6'
          >
            <CircleArrowLeft />
          </Button>
        </Link>
        <div>
          <h1 className='text-lg font-semibold'>Room Overview</h1>
          <p className='text-xs text-muted-foreground'>
            Operational schedule visibility by room
          </p>
        </div>
      </div>

      <hr />

      <RoomOverviewFiltersBar
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        instructors={instructors}
        onInstructorsChange={setInstructors}
        showEmptyRooms={showEmptyRooms}
        onShowEmptyRoomsChange={setShowEmptyRooms}
      />

      <div className='flex flex-col overflow-hidden'>
        {isInitialLoading && (
          <div className='flex h-[calc(100dvh-15rem)] min-h-[500px] items-center justify-center rounded-md border border-dashed border-border/60 bg-muted/20 lg:h-[calc(100dvh-19rem)]'>
            <Loader2 className='mr-2 size-5 animate-spin text-muted-foreground' />
            <p className='text-sm text-muted-foreground'>
              Loading room overview...
            </p>
          </div>
        )}

        {!isInitialLoading && data && (
          <>
            <RoomOverviewMobileCards rows={rows} />
            <div className='hidden md:block'>
              <RoomOverviewBoard
                rows={rows}
                shouldScrollToStart={shouldScrollToStart}
                onScrolledToStart={handleScrolledToStart}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
